export type ScheduledTransitionType = 'downgrade';

export interface ScheduledTransitionRecord {
  version: 1;
  type: ScheduledTransitionType;
  subscriptionId: string;
  activeUntil: string | null;
  targetPlanId: string | null;
  targetPlanName: string | null;
  createdAt: string;
}

const STORAGE_PREFIX = 'florentin.customer.scheduled-transition.v1';

const keyFor = (subscriptionId: string) => `${STORAGE_PREFIX}:${subscriptionId}`;

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function loadScheduledTransition(subscriptionId: string): ScheduledTransitionRecord | null {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(keyFor(subscriptionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScheduledTransitionRecord> | null;
    if (!parsed || parsed.version !== 1) return null;
    if (parsed.subscriptionId !== subscriptionId) return null;
    if (parsed.type !== 'downgrade') return null;
    return {
      version: 1,
      type: 'downgrade',
      subscriptionId,
      activeUntil: typeof parsed.activeUntil === 'string' ? parsed.activeUntil : null,
      targetPlanId: typeof parsed.targetPlanId === 'string' ? parsed.targetPlanId : null,
      targetPlanName: typeof parsed.targetPlanName === 'string' ? parsed.targetPlanName : null,
      createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveScheduledDowngrade(
  subscriptionId: string,
  activeUntil: string | null,
  targetPlan?: { id?: string | null; name?: string | null },
) {
  if (!canUseStorage()) return;

  const record: ScheduledTransitionRecord = {
    version: 1,
    type: 'downgrade',
    subscriptionId,
    activeUntil,
    targetPlanId: targetPlan?.id ?? null,
    targetPlanName: targetPlan?.name ?? null,
    createdAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(keyFor(subscriptionId), JSON.stringify(record));
  } catch {
    // ignore storage write failures
  }
}

export function clearScheduledTransition(subscriptionId: string) {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(keyFor(subscriptionId));
  } catch {
    // ignore storage failures
  }
}

export function isTransitionExpired(record: ScheduledTransitionRecord, now = new Date()): boolean {
  if (!record.activeUntil) return false;
  const parsed = new Date(record.activeUntil);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() <= now.getTime();
}
